const StyleE = () => {
  return (
    <div className="style-e">
      <style>{`
        .style-e {
          --bg-primary: #ffffff;
          --bg-secondary: #fafafa;
          --bg-soft: #f8f9fa;
          --text-primary: #1a1a1a;
          --text-secondary: #525252;
          --text-light: #737373;
          --accent: #000000;
          --accent-soft: #404040;
          --border: #e5e5e5;
          --border-soft: #f0f0f0;
        }
        
        .style-e.dark {
          --bg-primary: #ffffff;
          --bg-secondary: #f5f5f5;
          --bg-soft: #f0f0f0;
          --text-primary: #0a0a0a;
          --text-secondary: #404040;
          --text-light: #606060;
          --accent: #000000;
          --accent-soft: #2a2a2a;
          --border: #d5d5d5;
          --border-soft: #e8e8e8;
        }

        .style-e {
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          letter-spacing: -0.01em;
        }
        
        .style-e h1 {
          font-family: 'Inter', sans-serif;
          font-weight: 500;
          font-size: 2.5rem;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          color: var(--text-primary);
        }
        
        .style-e h2 {
          font-family: 'Inter', sans-serif;
          font-weight: 500;
          font-size: 1.75rem;
          line-height: 1.3;
          margin-bottom: 1rem;
          color: var(--text-primary);
        }
        
        .style-e h3 {
          font-family: 'Inter', sans-serif;
          font-weight: 500;
          font-size: 1.25rem;
          margin-bottom: 0.75rem;
          color: var(--text-secondary);
        }
        
        .style-e .open-section {
          margin-bottom: 3rem;
          padding: 2rem 0;
        }
        
        .style-e .breathe-card {
          background: var(--bg-soft);
          border: 1px solid var(--border-soft);
          border-radius: 12px;
          padding: 2.5rem;
          margin-bottom: 2rem;
          transition: all 0.2s ease;
        }
        
        .style-e .breathe-card:hover {
          border-color: var(--border);
          transform: translateY(-1px);
        }
        
        .style-e .gentle-emphasis {
          color: var(--accent-soft);
          font-weight: 500;
          padding: 0.125rem 0;
          border-bottom: 1px solid var(--border-soft);
        }
        
        .style-e .open-button {
          background: var(--accent);
          color: var(--bg-primary);
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          letter-spacing: -0.01em;
        }
        
        .style-e .open-button:hover {
          background: var(--accent-soft);
          transform: translateY(-1px);
        }
        
        .style-e .open-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin: 2rem 0;
        }
        
        .style-e .space-section {
          padding: 4rem 2rem;
          margin: 3rem 0;
          background: var(--bg-secondary);
          border-radius: 16px;
        }
        
        .style-e .quiet-accent {
          width: 40px;
          height: 2px;
          background: var(--text-light);
          margin: 1rem 0;
        }
        
        .style-e .approachable {
          font-size: 1.05rem;
          line-height: 1.65;
          color: var(--text-secondary);
          font-weight: 400;
        }
        
        .style-e .flexible-layout {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 3rem;
          align-items: start;
        }
        
        @media (max-width: 768px) {
          .style-e .flexible-layout {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
        }
      `}</style>
      
      <div className="max-w-5xl mx-auto px-8 py-12">
        <header className="text-center open-section">
          <h1>Open Minimal</h1>
          <div className="quiet-accent mx-auto"></div>
          <p className="approachable max-w-2xl mx-auto">
            Clean, approachable design that feels <span className="gentle-emphasis">effortlessly professional</span>. 
            Generous space and thoughtful typography create an inviting environment for exploring cultural collections.
          </p>
        </header>

        <section className="open-section">
          <div className="breathe-card">
            <h2>Thoughtful Simplicity</h2>
            <p className="approachable mb-6">
              This approach takes the clarity of minimal design but makes it more human and approachable. 
              Clean sans-serif typography, gentle curves, and plenty of breathing room create a space 
              that feels both professional and welcoming.
            </p>
            
            <div className="flexible-layout">
              <div>
                <h3>Design Philosophy</h3>
                <p style={{color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1rem'}}>
                  Every element serves a purpose, but nothing feels austere or intimidating. 
                  We prioritize <span className="gentle-emphasis">readability and flow</span> over 
                  visual drama, creating interfaces that get out of the way of great content.
                </p>
              </div>
              <div className="breathe-card" style={{margin: 0, background: 'var(--bg-primary)', border: '1px solid var(--border)'}}>
                <h3>Key Qualities</h3>
                <ul style={{listStyle: 'none', padding: 0, margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)'}}>
                  <li style={{marginBottom: '0.5rem'}}>• Effortless readability</li>
                  <li style={{marginBottom: '0.5rem'}}>• Gentle, human touches</li>
                  <li style={{marginBottom: '0.5rem'}}>• Flexible layouts</li>
                  <li>• Understated elegance</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="open-section">
          <h2 style={{textAlign: 'center', marginBottom: '2rem'}}>Interface Elements</h2>
          
          <div className="open-grid">
            <div className="breathe-card">
              <h3>Gentle Hierarchy</h3>
              <div className="quiet-accent"></div>
              <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem'}}>
                Typography that guides without commanding. Weight and size differences 
                are subtle but effective, creating natural reading flow.
              </p>
            </div>
            
            <div className="breathe-card">
              <h3>Soft Boundaries</h3>
              <div className="quiet-accent"></div>
              <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem'}}>
                Rounded corners and gentle borders define spaces without creating barriers. 
                Everything feels connected and approachable.
              </p>
            </div>
            
            <div className="breathe-card">
              <h3>Breathing Room</h3>
              <div className="quiet-accent"></div>
              <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem'}}>
                Generous spacing and thoughtful proportions give content room to be 
                absorbed without feeling sparse or empty.
              </p>
            </div>
          </div>
        </section>

        <section className="space-section text-center">
          <h2 style={{marginBottom: '1rem'}}>Dignified & Approachable</h2>
          <p className="approachable max-w-2xl mx-auto mb-8">
            Perfect for institutions that want to feel authoritative without being intimidating, 
            professional without being cold. This aesthetic invites exploration and learning.
          </p>
          <button className="open-button">
            Explore This Direction
          </button>
        </section>
      </div>
    </div>
  );
};

export default StyleE;